/**
 * All Test Scenarios Page - Tree View with Folder Hierarchy
 * Features: Folder tree sidebar, List view with column selector, Folder CRUD
 * Independent module with test_scenario folder type
 */

import React, { useState, useEffect, useRef } from 'react';
import { apiClient, TestScenarioWithEvidence, Folder } from '../services/api';
import { useLocation } from 'react-router-dom';
import { FolderTree } from '../components/FolderTree';
import { ColumnSelector, ColumnConfig } from '../components/ColumnSelector';
import { ScenarioImport } from '../components/ScenarioImport';
import { ProjectFolderStats } from '../components/ProjectFolderStats';
import { useRBAC } from '../components/ProtectedRoute';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { AIGenerateModal } from '../components/AIGenerateModal';
import { ReviewModal } from '../components/ReviewModal';
import { ScenarioViewModal } from '../components/ScenarioViewModal';
import { BulkEditModal, BulkEditPayload } from '../components/BulkEditModal';
import { FilterPanel } from '../components/FilterPanel';
import { RichTextEditor, RichTextDisplay, stripHtml } from '../components/RichTextEditor';
import { useAuth } from '../contexts/AuthContext';
import { parseJiraTickets } from '../utils/formatters';
import { ProductTour } from '../components/ProductTour';
import { useProductTour, isTourSeen, TOUR_IDS } from '../hooks/useProductTour';
import { scenariosTourSteps, foldersTourSteps } from '../tours/tourSteps';
import './AllScenariosPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

type Priority = 'Critical' | 'High' | 'Medium' | 'Low';
type Environment = 'Stage' | 'Preprod' | 'Prod';

const PREDEFINED_TAGS = [
  'Smoke', 'Sanity', 'Integration',
  'API', 'UI', 'Performance', 'Security', 'Functional', 'Manual'
];

interface ScenarioFormData {
  scenario_description: string;
  preconditions: string;
  expected_result: string;
  priority: Priority;
  environment: Environment | '';
  tags: string[];
}

interface FolderFormData {
  name: string;
  description: string;
  epic_id: string;
}

export const AllScenariosPage: React.FC = () => {
  const { canWrite } = useRBAC();
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const { confirm, dialog } = useConfirmDialog();
  const [reviewFolderId, setReviewFolderId] = useState<string | null>(null);
  const [reviewMode, setReviewMode] = useState<'submit' | 'review' | 'view'>('submit');
  const [allUsers, setAllUsers] = useState<{ email: string; name: string }[]>([]);
  const [viewingScenario, setViewingScenario] = useState<any | null>(null);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);

  // Folder Search (cross-folder flat results view)
  const [showFolderSearch, setShowFolderSearch] = useState(false);
  const [fsCreatedBy, setFsCreatedBy] = useState('');
  const [fsAiGenerated, setFsAiGenerated] = useState<'all' | 'yes' | 'no'>('all');
  const [fsDateMode, setFsDateMode] = useState<'all' | 'month' | 'custom'>('all');
  const [fsMonth, setFsMonth] = useState(''); // YYYY-MM
  const [fsDateFrom, setFsDateFrom] = useState('');
  const [fsDateTo, setFsDateTo] = useState('');
  const [fsReviewStatus, setFsReviewStatus] = useState('');
  const [fsResults, setFsResults] = useState<any | null>(null);
  const [fsLoading, setFsLoading] = useState(false);
  const [fsUsers, setFsUsers] = useState<string[]>([]);

  // Epic ID set modal
  const [epicModalFolderId, setEpicModalFolderId] = useState<string | null>(null);
  const [epicModalValue, setEpicModalValue] = useState('');
  const [epicModalSaving, setEpicModalSaving] = useState(false);
  // Epic ID search in search panel
  const [fsEpicId, setFsEpicId] = useState('');
  const [epicSearchResults, setEpicSearchResults] = useState<any | null>(null);
  const [epicSearchLoading, setEpicSearchLoading] = useState(false);
  // State
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  // A "leaf" (non-root) folder must be selected for most tour steps' targets
  // (Create Scenario, Import, Bulk Edit, Export, Share Link, Create Test Run) to exist —
  // those buttons only render for non-project folders. Auto-start waits for that; the
  // manual trigger auto-selects one so "Take a Tour" never breaks mid-way.
  const selectedIsLeafFolder = !!selectedFolderId && folders.find(f => f.id === selectedFolderId)?.level !== 0;
  const ensureLeafFolderSelected = () => {
    if (selectedIsLeafFolder) return;
    const leaf = folders.find(f => f.level !== 0);
    if (leaf) setSelectedFolderId(leaf.id);
  };
  const { run: tourRun, start: startTour, handleCallback: tourCallback } = useProductTour(TOUR_IDS.SCENARIOS, true, selectedIsLeafFolder);
  const { run: folderTourRun, start: startFolderTour, handleCallback: folderTourCallback } = useProductTour(TOUR_IDS.FOLDERS, false);

  // First-ever visit: nothing is selected by default, so the auto-play tour would wait
  // forever for a leaf folder. Proactively select one (once) so auto-play can actually fire.
  const autoTourFolderAttempted = useRef(false);
  useEffect(() => {
    if (autoTourFolderAttempted.current) return;
    if (isTourSeen(TOUR_IDS.SCENARIOS)) return;
    if (selectedFolderId || folders.length === 0) return;
    const leaf = folders.find(f => f.level !== 0);
    if (leaf) {
      autoTourFolderAttempted.current = true;
      setSelectedFolderId(leaf.id);
    }
  }, [folders, selectedFolderId]);

  const [scenarios, setScenarios] = useState<TestScenarioWithEvidence[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterEnvironment, setFilterEnvironment] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [sortField, setSortField] = useState<'id' | 'priority' | ''>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sc-sidebar-open');
    return saved === null ? false : saved !== 'true';
  });
  
  // Modals
  const [showScenarioForm, setShowScenarioForm] = useState(false);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [showRenameForm, setShowRenameForm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedScenarios, setSelectedScenarios] = useState<Set<string>>(new Set());
  const [editingScenario, setEditingScenario] = useState<TestScenarioWithEvidence | null>(null);
  const [folderFormParentId, setFolderFormParentId] = useState<string | null>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  
  // Form data
  const [scenarioFormData, setScenarioFormData] = useState<ScenarioFormData>({
    scenario_description: '',
    preconditions: '',
    expected_result: '',
    priority: 'Medium',
    environment: '',
    tags: []
  });
  const [isRegression, setIsRegression] = useState<boolean | null>(false);
  const [isAiGenerated, setIsAiGenerated] = useState<boolean | null>(false);
  
  const [folderFormData, setFolderFormData] = useState<FolderFormData>({
    name: '',
    description: '',
    epic_id: '',
  });
  
  // Tags
  const [tagInput, setTagInput] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const tagInputRef = useRef<HTMLInputElement>(null);
  
  // Evidence files
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  
  // Column configuration
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'id', label: 'Scenario ID', visible: true, required: true },
    { key: 'preconditions', label: 'Precondition', visible: false },
    { key: 'description', label: 'Title', visible: true, required: true },
    { key: 'priority', label: 'Priority', visible: true },
    { key: 'environment', label: 'Environment', visible: false },
    { key: 'tags', label: 'Tags', visible: false },
    { key: 'expected', label: 'Expected Result', visible: false },
    { key: 'created_date', label: 'Created Date', visible: true },
    { key: 'created_by', label: 'Created By', visible: false },
    { key: 'actions', label: 'Actions', visible: true, required: true }
  ]);

  // Load folders on mount, auto-select folder from query param (shared link / notification)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sharedFolderId = params.get('folder_id');
    if (folders.length > 0 && sharedFolderId) {
      // Folders already loaded — just update selection
      setSelectedFolderId(sharedFolderId);
    } else {
      loadFolders(sharedFolderId || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Load scenarios when folder selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedFolderId) {
      loadScenarios();
    } else {
      setScenarios([]);
    }
  }, [selectedFolderId]);

  // Load column preferences from localStorage
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const saved = localStorage.getItem('test-scenarios-columns');
    if (saved) {
      try {
        const visibilityMap = JSON.parse(saved);
        setColumns(cols => cols.map(col => ({
          ...col,
          visible: visibilityMap[col.key] ?? col.visible
        })));
      } catch (e) {
        console.error('Failed to load column preferences:', e);
      }
    }
  }, []);

  const loadFolders = async (autoSelectId?: string) => {
    try {
      const data = await apiClient.getFolders('test_scenario'); // Only load test_scenario folders
      setFolders(data);
      if (autoSelectId) {
        setSelectedFolderId(autoSelectId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folders');
    }
  };

  const loadScenarios = async () => {
    if (!selectedFolderId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.getScenariosByFolder(selectedFolderId);
      setScenarios(data);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load scenarios';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsLoading(false);
    }
  };

  // Load QA_Engineer + Admin users for the Created By filter (Viewers excluded)
  const loadFsUsers = async () => {
    if (fsUsers.length > 0) return;
    try {
      const users = await apiClient.getUsersForReview();
      setFsUsers(users.map((u: any) => u.email));
    } catch { /* silently ignore */ }
  };

  const handleFolderSearch = async () => {
    setFsLoading(true);
    setFsResults(null);
    try {
      const params: any = {};
      if (fsCreatedBy) params.created_by = fsCreatedBy;
      if (fsAiGenerated === 'yes') params.ai_generated = true;
      if (fsAiGenerated === 'no') params.ai_generated = false;
      if (fsDateMode === 'month' && fsMonth) {
        // Convert YYYY-MM to a from/to range for the full month
        params.date_from = `${fsMonth}-01`;
        const [y, m] = fsMonth.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        params.date_to = `${fsMonth}-${String(lastDay).padStart(2, '0')}`;
      } else if (fsDateMode === 'custom') {
        if (fsDateFrom) params.date_from = fsDateFrom;
        if (fsDateTo) params.date_to = fsDateTo;
      }
      if (fsReviewStatus) params.review_status = fsReviewStatus;
      const result = await apiClient.searchScenarioFolders(params);
      setFsResults(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Search failed');
    } finally {
      setFsLoading(false);
    }
  };

  const handleClearFolderSearch = () => {
    setFsCreatedBy('');
    setFsAiGenerated('all');
    setFsDateMode('all');
    setFsMonth('');
    setFsDateFrom('');
    setFsDateTo('');
    setFsReviewStatus('');
    setFsResults(null);
    setFsEpicId('');
    setEpicSearchResults(null);
  };

  // Epic ID handlers
  const handleSetEpicId = (folderId: string, currentEpicId?: string) => {
    setEpicModalFolderId(folderId);
    setEpicModalValue(currentEpicId || '');
  };

  const handleSaveEpicId = async () => {
    if (!epicModalFolderId) return;
    setEpicModalSaving(true);
    try {
      await apiClient.setFolderEpicId(epicModalFolderId, epicModalValue.trim() || null);
      await loadFolders();
      setEpicModalFolderId(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save Epic ID');
    } finally {
      setEpicModalSaving(false);
    }
  };

  const handleEpicSearch = async () => {
    if (!fsEpicId.trim()) return;
    setEpicSearchLoading(true);
    setEpicSearchResults(null);
    try {
      const result = await apiClient.searchFoldersByEpic(fsEpicId.trim());
      setEpicSearchResults(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Epic search failed');
    } finally {
      setEpicSearchLoading(false);
    }
  };

  const getBreadcrumb = (): Folder[] => {
    if (!selectedFolderId) return [];
    
    const path: Folder[] = [];
    // eslint-disable-next-line no-loop-func
    let current = folders.find(f => f.id === selectedFolderId);
    
    while (current) {
      path.unshift(current);
      // eslint-disable-next-line no-loop-func
      current = current.parent_id ? folders.find(f => f.id === current!.parent_id) : undefined;
    }
    
    return path;
  };

  // Scenario CRUD
  const handleCreateScenario = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    setEditingScenario(null);
    setScenarioFormData({ scenario_description: '', preconditions: '', expected_result: '', priority: 'Medium', environment: '', tags: [] });
    setIsRegression(false);
    setIsAiGenerated(false);
    setTagInput('');
    setEvidenceFiles([]);
    setShowScenarioForm(true);
  };

  const handleEditScenario = (scenario: TestScenarioWithEvidence) => {
    setEditingScenario(scenario);
    const tags = scenario.tags || [];
    setScenarioFormData({
      scenario_description: scenario.scenario_description,
      preconditions: scenario.preconditions || '',
      expected_result: scenario.expected_result || '',
      priority: scenario.priority,
      environment: scenario.environment || '',
      // 'Manual' is a regular selectable tag (like Smoke/Sanity) — only Regression and
      // AI Generated are stripped here since those are tracked via their own toggles below.
      tags: tags.filter(t => t !== 'Regression' && t !== 'AI Generated')
    });
    setIsRegression(tags.includes('Regression') ? true : false);
    setIsAiGenerated(tags.includes('AI Generated') ? true : false);
    setTagInput('');
    setEvidenceFiles([]);
    setShowScenarioForm(true);
  };

  const handleSubmitScenario = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolderId) return;

    // Validate mandatory fields — tags are optional
    if (isRegression === null) {
      setError('Please select whether this is a Regression test');
      return;
    }
    if (isAiGenerated === null) {
      setError('Please select whether this was AI Generated');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Merge Regression and AI Generated back into tags
    const finalTags = [...scenarioFormData.tags];
    if (isRegression) finalTags.push('Regression');
    if (isAiGenerated) finalTags.push('AI Generated');

    try {
      const submitData = {
        ...scenarioFormData,
        tags: finalTags,
        preconditions: scenarioFormData.preconditions || undefined,
        expected_result: scenarioFormData.expected_result || undefined,
        environment: scenarioFormData.environment || undefined
      };

      if (editingScenario) {
        await apiClient.updateScenario(editingScenario.id, submitData);
      } else {
        await apiClient.createScenario({ folder_id: selectedFolderId, ...submitData });
      }

      setShowScenarioForm(false);
      setEditingScenario(null);
      setEvidenceFiles([]);
      setIsRegression(null);
      setIsAiGenerated(null);
      await loadScenarios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save scenario');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    const confirmed = await confirm('Delete Scenario', 'Are you sure you want to delete this scenario? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    try {
      await apiClient.deleteScenario(scenarioId);
      await loadScenarios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete scenario');
    }
  };

  const handleBulkDeleteScenarios = async () => {
    if (selectedScenarios.size === 0) {
      setError('Please select at least one scenario to delete');
      return;
    }

    const count = selectedScenarios.size;
    const confirmed = await confirm('Delete Scenarios', `Are you sure you want to delete ${count} selected scenario(s)? This action cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const scenarioIds = Array.from(selectedScenarios);
      const result = await apiClient.bulkDeleteScenarios(scenarioIds);
      
      if (result.failed_ids.length > 0) {
        setError(`Deleted ${result.deleted_count} of ${result.total_requested} scenarios. ${result.failed_ids.length} failed.`);
      }
      
      setSelectedScenarios(new Set()); // Clear selection
      await loadScenarios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete scenarios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkEditScenarios = async (payload: BulkEditPayload) => {
    const ids = Array.from(selectedScenarios);
    const result = await apiClient.bulkUpdateScenarios({ scenario_ids: ids, ...payload });
    if (result.failed_ids.length > 0) {
      setError(`Updated ${result.updated_count} of ${result.total_requested} scenarios. ${result.failed_ids.length} failed.`);
    }
    setShowBulkEditModal(false);
    setSelectedScenarios(new Set());
    await loadScenarios();
  };
  const handleCreateSubfolder = (parentId: string) => {
    setFolderFormParentId(parentId);
    setFolderFormData({ name: '', description: '', epic_id: '' });
    setShowFolderForm(true);
  };

  const handleSubmitFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);
    setError(null);

    try {
      await apiClient.createFolder({
        name: folderFormData.name,
        description: folderFormData.description,
        epic_id: folderFormData.epic_id.trim().toUpperCase() || undefined,
        parent_id: folderFormParentId || undefined,
        folder_type: 'test_scenario'
      });
      
      setShowFolderForm(false);
      setFolderFormParentId(null);
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameFolder = (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    if (folder) {
      setRenamingFolderId(folderId);
      setFolderFormData({ name: folder.name, description: folder.description || '', epic_id: folder.epic_id || '' });
      setShowRenameForm(true);
    }
  };

  const handleSubmitRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renamingFolderId) return;
    
    setIsLoading(true);
    setError(null);

    try {
      await apiClient.updateFolder(renamingFolderId, {
        name: folderFormData.name,
        description: folderFormData.description,
        epic_id: folderFormData.epic_id.trim().toUpperCase() || undefined,
      });
      
      setShowRenameForm(false);
      setRenamingFolderId(null);
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportExcel = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    setShowImportModal(true);
  };

  const handleImportComplete = async () => {
    setShowImportModal(false);
    await loadScenarios();
  };

  const handleExportScenarios = () => {
    if (!selectedFolderId) { setError('Please select a folder first'); return; }
    if (selectedScenarios.size === 0) { setError('Please select at least one scenario to export'); return; }

    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const XLSX = require('xlsx');

      // Only export selected scenarios
      const toExport = filteredScenarios.filter(sc => selectedScenarios.has(sc.id));

      // Column key → { header, getValue }
      const COL_MAP: Record<string, { header: string; getValue: (sc: any) => any }> = {
        id:           { header: 'Scenario ID',     getValue: sc => sc.scenario_id },
        description:  { header: 'Title',           getValue: sc => sc.scenario_description },
        priority:     { header: 'Priority',        getValue: sc => sc.priority },
        environment:  { header: 'Environment',     getValue: sc => sc.environment || '' },
        tags:         { header: 'Tags',            getValue: sc => (sc.tags || []).join(', ') },
        preconditions: { header: 'Precondition',   getValue: sc => sc.preconditions || '' },
        expected:     { header: 'Expected Result', getValue: sc => sc.expected_result || '' },
        evidence:     { header: 'Evidence Count',  getValue: sc => (sc.expected_evidence || []).length },
        created_date: { header: 'Created Date',    getValue: sc => new Date(sc.created_at).toLocaleDateString() },
        created_by:   { header: 'Created By',      getValue: sc => sc.created_by },
      };

      // Use only visible columns (in order), skip required-only cols like actions/checkbox
      const visibleCols = columns.filter(c => c.visible && COL_MAP[c.key]);

      // Build rows
      const rows = toExport.map(sc => {
        const row: Record<string, any> = {};
        visibleCols.forEach(col => {
          row[COL_MAP[col.key].header] = COL_MAP[col.key].getValue(sc);
        });
        return row;
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Scenarios');
      XLSX.writeFile(wb, `scenarios_${new Date().toISOString().split('T')[0]}.xlsx`);
      setSelectedScenarios(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export scenarios');
    }
  };

  const handleShareLink = async () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    try {
      const path = getBreadcrumb().map((f: any) => encodeURIComponent(f.name)).join('/');
      const link = `${window.location.origin}/test-scenarios/${path}?folder_id=${selectedFolderId}`;
      setShareLink(link);
      setShowShareModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get share link');
    }
  };

  const handleCreateTestRun = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    window.location.href = `/test-runs/create?folder_id=${selectedFolderId}&type=scenario`;
  };

  const handleToggleScenario = (scenarioId: string) => {
    const newSelected = new Set(selectedScenarios);
    if (newSelected.has(scenarioId)) {
      newSelected.delete(scenarioId);
    } else {
      newSelected.add(scenarioId);
    }
    setSelectedScenarios(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedScenarios.size === filteredScenarios.length) {
      setSelectedScenarios(new Set());
    } else {
      setSelectedScenarios(new Set(filteredScenarios.map(s => s.id)));
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    if (!folder) return;
    
    const hasChildren = folders.some(f => f.parent_id === folderId);
    const message = hasChildren
      ? `Delete "${folder.name}" and all its subfolders and scenarios? This cannot be undone.`
      : `Delete "${folder.name}" and all its scenarios? This cannot be undone.`;
    
    const confirmed = await confirm('Delete Folder', message);
    if (!confirmed) {
      return;
    }

    try {
      await apiClient.deleteFolder(folderId);
      if (selectedFolderId === folderId) {
        setSelectedFolderId(null);
      }
      await loadFolders();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to delete folder';
      setError(errorMessage);
    }
  };

  const handleMoveFolder = async (folderId: string, newParentId: string | null) => {
    try {
      await apiClient.moveFolder(folderId, newParentId);
      await loadFolders();
    } catch (err: any) {
      setError(String(err?.response?.data?.detail || err?.message || 'Failed to move folder'));
    }
  };

  const handleSubmitReview = async (folderId: string) => {
    if (allUsers.length === 0) {
      try {
        const users = await apiClient.getAllUsers();
        setAllUsers(users.map((u: any) => ({ email: u.email, name: u.name })));
      } catch {}
    }
    setReviewFolderId(folderId);
    setReviewMode('submit');
  };

  const handleReviewAction = (folderId: string) => {
    setReviewFolderId(folderId);
    setReviewMode('review');
  };

  // Tags
  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !scenarioFormData.tags.includes(tag)) {
      setScenarioFormData({ ...scenarioFormData, tags: [...scenarioFormData.tags, tag] });
      setTagInput('');
      setShowTagSuggestions(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRemoveTag = (tagToRemove: string) => {
    setScenarioFormData({
      ...scenarioFormData,
      tags: scenarioFormData.tags.filter(t => t !== tagToRemove)
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSelectSuggestion = (tag: string) => {
    if (!scenarioFormData.tags.includes(tag)) {
      setScenarioFormData({ ...scenarioFormData, tags: [...scenarioFormData.tags, tag] });
    }
    setTagInput('');
    setShowTagSuggestions(false);
    tagInputRef.current?.focus();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filteredSuggestions = PREDEFINED_TAGS.filter(
    tag => tag.toLowerCase().includes(tagInput.toLowerCase()) && !scenarioFormData.tags.includes(tag)
  );

  // Evidence files
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setEvidenceFiles(Array.from(e.target.files));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRemoveFile = (index: number) => {
    setEvidenceFiles(files => files.filter((_, i) => i !== index));
  };

  // Filtering
  const PRIORITY_ORDER: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };

  const filteredScenarios = (() => {
    let list = searchTerm
      ? scenarios.filter(sc =>
          sc.scenario_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sc.scenario_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (sc.expected_result && stripHtml(sc.expected_result).toLowerCase().includes(searchTerm.toLowerCase())) ||
          (sc.tags && sc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
        )
      : [...scenarios];

    if (filterPriority) list = list.filter(sc => sc.priority === filterPriority);
    if (filterEnvironment) list = list.filter(sc => sc.environment === filterEnvironment);
    if (filterTag) {
      if (filterTag === 'Manual') {
        // Manual = scenarios that do NOT have the "AI Generated" tag
        list = list.filter(sc => !sc.tags || !sc.tags.includes('AI Generated'));
      } else {
        list = list.filter(sc => sc.tags && sc.tags.includes(filterTag));
      }
    }

    if (sortField === 'id') {
      list.sort((a, b) => {
        const na = parseInt(a.scenario_id.replace(/\D/g, '')) || 0;
        const nb = parseInt(b.scenario_id.replace(/\D/g, '')) || 0;
        return sortDir === 'asc' ? na - nb : nb - na;
      });
    } else if (sortField === 'priority') {
      list.sort((a, b) => {
        const pa = PRIORITY_ORDER[a.priority] ?? 99;
        const pb = PRIORITY_ORDER[b.priority] ?? 99;
        return sortDir === 'asc' ? pa - pb : pb - pa;
      });
    }
    return list;
  })();

  const handleSort = (field: 'id' | 'priority') => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sortIcon = (field: 'id' | 'priority') => {
    if (sortField !== field) return <span style={{ color: '#d1d5db', marginLeft: 4 }}>↕</span>;
    return <span style={{ color: '#1e3a8a', marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const getEnvironmentColor = (env: string): string => {
    switch (env) {
      case 'Stage': return '#17a2b8';
      case 'Preprod': return '#ffc107';
      case 'Prod': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const breadcrumb = getBreadcrumb();

  return (
    <div className="test-scenarios-page-tree">
      <ProductTour steps={scenariosTourSteps} run={tourRun} onCallback={tourCallback} />
      <ProductTour steps={foldersTourSteps} run={folderTourRun} onCallback={folderTourCallback} />
      {dialog}
      {error && (
        <div className="error-banner">
          {typeof error === 'string' ? error : JSON.stringify(error)}
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      <div className="test-scenarios-layout">
        {/* Left Sidebar - Folder Tree */}
        <div className={`test-scenarios-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-inner" data-tour="scenario-folder-tree">
            {/* Folder Search toggle */}
            {!sidebarCollapsed && (
              <button
                className={`fs-search-toggle-btn ${showFolderSearch ? 'active' : ''}`}
                onClick={() => {
                  setShowFolderSearch(v => !v);
                  if (!showFolderSearch) loadFsUsers();
                }}
                title="Search across all folders"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                {showFolderSearch ? 'Close Search' : 'Search Folders'}
              </button>
            )}
            <FolderTree
              folders={folders}
              selectedFolderId={selectedFolderId}
              onSelectFolder={setSelectedFolderId}
              onCreateSubfolder={handleCreateSubfolder}
              onRenameFolder={handleRenameFolder}
              onDeleteFolder={handleDeleteFolder}
              onMoveFolder={handleMoveFolder}
              onSubmitReview={handleSubmitReview}
              onReviewAction={handleReviewAction}
              onSetEpicId={canWrite ? handleSetEpicId : undefined}
              currentUserEmail={currentUser?.email}
            />
          </div>
          <button
            className="sidebar-toggle-btn"
            onClick={() => setSidebarCollapsed(prev => {
              localStorage.setItem('sc-sidebar-open', String(prev));
              return !prev;
            })}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              {sidebarCollapsed ? <path d="M9 18l6-6-6-6"/> : <path d="M15 18l-6-6 6-6"/>}
            </svg>
          </button>
        </div>

        {/* Right Content Area */}
        <div className="test-scenarios-content">
          {/* ── Folder Search Results View ── */}
          {showFolderSearch && (
            <div className="fs-panel">
              <div className="fs-panel-header">
                <h2 className="fs-panel-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  Search Across Folders
                </h2>
                <p className="fs-panel-sub">Filter folders by who wrote the scenarios, AI vs Manual, priority, and more.</p>
              </div>

              <div className="fs-filters">
                {/* Created By */}
                <div className="fs-filter-group">
                  <label>Created By</label>
                  <select value={fsCreatedBy} onChange={e => setFsCreatedBy(e.target.value)} className="fs-select">
                    <option value="">All Users</option>
                    {fsUsers.map(u => <option key={u} value={u}>{u.split('@')[0]}</option>)}
                  </select>
                </div>

                {/* AI Generated */}
                <div className="fs-filter-group">
                  <label>AI vs Manual</label>
                  <select value={fsAiGenerated} onChange={e => setFsAiGenerated(e.target.value as any)} className="fs-select">
                    <option value="all">All</option>
                    <option value="yes">AI Generated</option>
                    <option value="no">Manual</option>
                  </select>
                </div>

                {/* Date Filter */}
                <div className="fs-filter-group">
                  <label>Date Range</label>
                  <select value={fsDateMode} onChange={e => setFsDateMode(e.target.value as any)} className="fs-select">
                    <option value="all">All Time</option>
                    <option value="month">By Month</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {/* Month picker */}
                {fsDateMode === 'month' && (
                  <div className="fs-filter-group">
                    <label>Month</label>
                    <input
                      type="month"
                      value={fsMonth}
                      onChange={e => setFsMonth(e.target.value)}
                      className="fs-select"
                    />
                  </div>
                )}

                {/* Custom date range */}
                {fsDateMode === 'custom' && (
                  <>
                    <div className="fs-filter-group">
                      <label>From</label>
                      <input
                        type="date"
                        value={fsDateFrom}
                        onChange={e => setFsDateFrom(e.target.value)}
                        className="fs-select"
                      />
                    </div>
                    <div className="fs-filter-group">
                      <label>To</label>
                      <input
                        type="date"
                        value={fsDateTo}
                        onChange={e => setFsDateTo(e.target.value)}
                        className="fs-select"
                      />
                    </div>
                  </>
                )}

                {/* Review Status */}
                <div className="fs-filter-group">
                  <label>Review Status</label>
                  <select value={fsReviewStatus} onChange={e => setFsReviewStatus(e.target.value)} className="fs-select">
                    <option value="">All</option>
                    {['Draft', 'In Review', 'Approved', 'Changes Requested'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="fs-filter-actions">
                  <button className="fs-btn-search" onClick={handleFolderSearch} disabled={fsLoading}>
                    {fsLoading ? 'Searching...' : 'Search'}
                  </button>
                  <button className="fs-btn-clear" onClick={handleClearFolderSearch}>Clear</button>
                </div>
              </div>

              {/* Epic ID Search — separate quick-search section */}
              <div className="fs-epic-section">
                <div className="fs-epic-label">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                  </svg>
                  Search by Epic ID
                </div>
                <div className="fs-epic-row">
                  <input
                    type="text"
                    className="fs-select"
                    placeholder="e.g. PLANNER-EP-12"
                    value={fsEpicId}
                    onChange={e => { setFsEpicId(e.target.value.toUpperCase()); setEpicSearchResults(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleEpicSearch(); }}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <button className="fs-btn-search" onClick={handleEpicSearch} disabled={epicSearchLoading || !fsEpicId.trim()}>
                    {epicSearchLoading ? '...' : 'Find'}
                  </button>
                </div>

                {epicSearchResults && (
                  <div className="fs-epic-results">
                    <div className="fs-results-summary">
                      <span className="fs-stat">Epic <strong>{epicSearchResults.epic_id}</strong></span>
                      <span className="fs-stat-sep">·</span>
                      <span className="fs-stat"><strong>{epicSearchResults.total}</strong> folder{epicSearchResults.total !== 1 ? 's' : ''}</span>
                    </div>
                    {epicSearchResults.folders.length === 0 ? (
                      <div className="fs-empty">No folders mapped to this Epic ID.</div>
                    ) : (
                      <table className="fs-table">
                        <thead>
                          <tr><th>Folder</th><th>Project</th><th>Review</th><th></th></tr>
                        </thead>
                        <tbody>
                          {epicSearchResults.folders.map((r: any) => {
                            const statusColors: Record<string, string> = {
                              'Approved': '#2ecc71', 'In Review': '#29b6f6',
                              'Changes Requested': '#f05a6e', 'Draft': '#9ca3af'
                            };
                            return (
                              <tr key={r.folder_id} className="fs-result-row">
                                <td className="fs-td-path"><span className="fs-folder-path">{r.folder_name}</span></td>
                                <td className="fs-td-path" style={{ color: '#6b7280', fontWeight: 500 }}>{r.project_name}</td>
                                <td>
                                  <span className="fs-review-badge" style={{ background: statusColors[r.review_status] || '#9ca3af' }}>
                                    {r.review_status}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    className="fs-btn-open"
                                    onClick={() => {
                                      setSelectedFolderId(r.folder_id);
                                      setShowFolderSearch(false);
                                      setEpicSearchResults(null);
                                    }}
                                  >Open →</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>

              {/* Results */}
              {fsResults && (
                <div className="fs-results">
                  <div className="fs-results-summary">
                    <span className="fs-stat"><strong>{fsResults.total_folders}</strong> folder{fsResults.total_folders !== 1 ? 's' : ''}</span>
                    <span className="fs-stat-sep">·</span>
                    <span className="fs-stat"><strong>{fsResults.total_scenarios}</strong> scenario{fsResults.total_scenarios !== 1 ? 's' : ''}</span>
                  </div>

                  {fsResults.results.length === 0 ? (
                    <div className="fs-empty">No folders match the selected filters.</div>
                  ) : (
                    <table className="fs-table">
                      <thead>
                        <tr>
                          <th>Folder Path</th>
                          <th>Review</th>
                          <th>Total</th>
                          <th>AI</th>
                          <th>Manual</th>
                          <th>Contributors</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {fsResults.results.map((r: any) => {
                          const statusColors: Record<string, string> = {
                            'Approved': '#2ecc71', 'In Review': '#29b6f6',
                            'Changes Requested': '#f05a6e', 'Draft': '#9ca3af'
                          };
                          return (
                            <tr key={r.folder_id} className="fs-result-row">
                              <td className="fs-td-path">
                                <span className="fs-folder-path">{r.folder_path}</span>
                              </td>
                              <td>
                                <span className="fs-review-badge" style={{ background: statusColors[r.review_status] || '#9ca3af' }}>
                                  {r.review_status}
                                </span>
                              </td>
                              <td className="fs-td-num">{r.total}</td>
                              <td className="fs-td-num fs-ai">{r.ai_count}</td>
                              <td className="fs-td-num fs-manual">{r.manual_count}</td>
                              <td className="fs-td-contributors">
                                {r.contributors.map((c: string) => (
                                  <span key={c} className="fs-contributor">{c.split('@')[0]}</span>
                                ))}
                              </td>
                              <td>
                                <button
                                  className="fs-btn-open"
                                  onClick={() => {
                                    setSelectedFolderId(r.folder_id);
                                    setShowFolderSearch(false);
                                    setFsResults(null);
                                  }}
                                  title="Open this folder"
                                >
                                  Open →
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}

          {!showFolderSearch && selectedFolderId && (
            <>
              {/* Breadcrumb */}
              <div className="content-breadcrumb">
                <span className="breadcrumb-icon">📍</span>
                {breadcrumb.map((folder, index) => (
                  <React.Fragment key={folder.id}>
                    {index > 0 && <span className="breadcrumb-separator">›</span>}
                    <span className="breadcrumb-item">
                      {parseJiraTickets(folder.name).map((seg, i) =>
                        typeof seg === 'string'
                          ? <React.Fragment key={i}>{seg}</React.Fragment>
                          : <a
                              key={i}
                              href={seg.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="breadcrumb-jira-link"
                              onClick={e => e.stopPropagation()}
                              title={`Open ${seg.ticket} in Jira`}
                            >{seg.ticket}</a>
                      )}
                    </span>
                  </React.Fragment>
                ))}
                {(() => {
                  const sf = folders.find(f => f.id === selectedFolderId);
                  const status = sf?.review_status || 'Draft';
                  const cfg: Record<string, { bg: string; icon: React.ReactNode; label: string }> = {
                    'Draft': {
                      bg: '#6b7280',
                      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
                      label: 'Draft'
                    },
                    'In Review': {
                      bg: '#29b6f6',
                      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
                      label: 'In Review'
                    },
                    'Approved': {
                      bg: '#2ecc71',
                      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="0"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5l-4.5-4.5 1.41-1.41L10 13.67l7.09-7.09 1.41 1.41L10 16.5z"/></svg>,
                      label: 'Approved'
                    },
                    'Changes Requested': {
                      bg: '#f05a6e',
                      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>,
                      label: 'Change Requested'
                    },
                  };
                  const c = cfg[status] || cfg['Draft'];
                  const pillF = folders.find(f => f.id === selectedFolderId);
                  const canOpenReview = status !== 'Draft' && pillF;
                  const handlePillClick = () => {
                    if (!pillF || !canOpenReview) return;
                    if (status === 'In Review' && (pillF.reviewer_email === currentUser?.email || currentUser?.role === 'Admin')) {
                      // Assigned reviewer OR Admin can take review action
                      handleReviewAction(pillF.id);
                    } else if (status === 'In Review') {
                      // Submitter/others see read-only view showing who the reviewer is
                      setReviewFolderId(pillF.id);
                      setReviewMode('view');
                    } else if (status === 'Approved') {
                      setReviewFolderId(pillF.id);
                      setReviewMode('view');
                    } else if (status === 'Changes Requested' && pillF.submitted_by === currentUser?.email) {
                      setReviewFolderId(pillF.id);
                      setReviewMode('submit');
                    }
                  };
                  return (
                    <span
                      className="review-status-pill"
                      style={{ background: c.bg, cursor: canOpenReview ? 'pointer' : 'default' }}
                      onClick={handlePillClick}
                      title={canOpenReview ? 'Click to view review details' : ''}
                    >
                      {c.icon}{c.label}
                    </span>
                  );
                })()}
              </div>

              {/* Folder description — shown below breadcrumb if set */}
              {(() => {
                const sf = folders.find(f => f.id === selectedFolderId);
                if (!sf?.description) return null;
                return (
                  <div className="folder-description-bar" title={sf.description}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.5 }}>
                      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                    </svg>
                    <span className="folder-description-text">{sf.description}</span>
                  </div>
                );
              })()}

              {/* Action Bar */}
              <div className="content-actions">
                {canWrite && (
                  <button className="btn btn-secondary" data-tour="create-subfolder-btn" onClick={() => handleCreateSubfolder(selectedFolderId)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>
                    Create Subfolder
                  </button>
                )}
                {(() => {
                  const selectedFolder = folders.find(f => f.id === selectedFolderId);
                  const isProjectFolder = selectedFolder?.level === 0;
                  return canWrite && !isProjectFolder && (
                    <>
                      <button className="btn btn-primary" data-tour="create-scenario-btn" onClick={handleCreateScenario}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Create Scenario
                      </button>
                      <button className="btn btn-ai" onClick={() => setShowAIModal(true)}>
                        ✨ Generate with AI
                      </button>
                    </>
                  );
                })()}
                <button
                  className="btn btn-secondary"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => { ensureLeafFolderSelected(); startFolderTour(); }}
                  title="Learn about folder management: subfolders, rename, move, review, share links"
                >
                  🗂️ Folder Tips
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => { ensureLeafFolderSelected(); startTour(); }}
                  title="Replay the guided tour for this page"
                >
                  🎯 Take a Tour
                </button>
              </div>
              {(() => {
                const selectedFolder = folders.find(f => f.id === selectedFolderId);
                const isProjectFolder = selectedFolder?.level === 0;
                return canWrite && !isProjectFolder && (
                  <div className="action-bar">
                    <button className="btn btn-action" data-tour="import-excel-btn" onClick={handleImportExcel} title="Import scenarios from Excel">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      Import Excel
                    </button>
                    <button
                      className="btn btn-action"
                      data-tour="export-btn"
                      onClick={handleExportScenarios}
                      disabled={selectedScenarios.size === 0}
                      title={selectedScenarios.size === 0 ? "Select scenarios to export" : `Export ${selectedScenarios.size} selected scenario(s)`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Export ({selectedScenarios.size})
                    </button>
                    <button
                      className="btn btn-action btn-delete"
                      onClick={handleBulkDeleteScenarios}
                      disabled={selectedScenarios.size === 0}
                      title={selectedScenarios.size === 0 ? "Select scenarios to delete" : `Delete ${selectedScenarios.size} selected scenario(s)`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      Delete Selected ({selectedScenarios.size})
                    </button>
                    <button
                      className="btn btn-action"
                      data-tour="bulk-edit-btn"
                      onClick={() => setShowBulkEditModal(true)}
                      disabled={selectedScenarios.size < 2}
                      title={selectedScenarios.size < 2 ? "Select 2+ scenarios to bulk edit" : `Bulk edit ${selectedScenarios.size} scenarios`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Bulk Edit ({selectedScenarios.size})
                    </button>
                    <div className="action-bar-divider" />
                    <button className="btn btn-action" data-tour="share-link-btn" onClick={handleShareLink} title="Get shareable link for this folder">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                      Share Link
                    </button>
                    {(() => {
                      const sf = folders.find(f => f.id === selectedFolderId);
                      const approved = sf?.review_status === 'Approved';
                      return (
                        <button
                          className="btn btn-action"
                          data-tour="create-test-run-btn"
                          onClick={approved ? handleCreateTestRun : undefined}
                          disabled={!approved}
                          title={approved ? 'Create test run from scenarios' : `Folder is "${sf?.review_status || 'Draft'}" — must be Approved to create a Test Run`}
                          style={!approved ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={approved ? '#3b82f6' : '#9ca3af'} strokeWidth="2"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
                          Create Test Run
                        </button>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* Show stats for project folders (level 0), otherwise show controls */}
              {(() => {
                const selectedFolder = folders.find(f => f.id === selectedFolderId);
                const isProjectFolder = selectedFolder?.level === 0;
                
                if (isProjectFolder) {
                  return <ProjectFolderStats folderId={selectedFolderId} folderType="test_scenario" />;
                }
                
                return (
                  <>
                    {/* Controls Bar */}
                    <div className="content-controls">
                      <input
                        type="text"
                        className="search-input search-input-sm"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />

                      <FilterPanel
                        filters={[
                          { key: 'priority', label: 'Priority', options: ['Critical','High','Medium','Low'], value: filterPriority, onChange: setFilterPriority },
                          { key: 'environment', label: 'Environment', options: ['Stage','Preprod','Prod'], value: filterEnvironment, onChange: setFilterEnvironment },
                          { key: 'tag', label: 'Tag', options: ['Smoke','Sanity','Integration','API','UI','Performance','Security','Functional','Regression','AI Generated','Manual'], value: filterTag, onChange: setFilterTag },
                        ]}
                        onClearAll={() => { setFilterPriority(''); setFilterEnvironment(''); setFilterTag(''); }}
                      />

                      <ColumnSelector columns={columns} onColumnsChange={setColumns} storageKey="test-scenarios-columns" />

                      <div className="stats-badge">Total: <strong>{scenarios.length}</strong></div>
                      {(searchTerm || filterPriority || filterEnvironment || filterTag) && filteredScenarios.length !== scenarios.length && (
                        <div className="stats-badge filtered">Filtered: <strong>{filteredScenarios.length}</strong></div>
                      )}
                    </div>

                    {/* Scenarios List */}
                    {isLoading ? (
                      <div className="content-loading">Loading scenarios...</div>
                    ) : filteredScenarios.length === 0 ? (
                      <div className="content-empty">
                        <p>{searchTerm ? 'No scenarios match your search' : 'No scenarios in this folder'}</p>
                        <button className="btn btn-primary" onClick={handleCreateScenario}>
                          Create First Scenario
                        </button>
                      </div>
                    ) : (
                      <div className="test-scenarios-table-container">
                        <div className="test-scenarios-table-wrapper">
                        <table className="test-scenarios-table">
                          <thead>
                            <tr>
                              <th className="col-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedScenarios.size === filteredScenarios.length && filteredScenarios.length > 0}
                                  onChange={handleSelectAll}
                                  title="Select all"
                                />
                              </th>
                              {columns.filter(col => col.visible).map(col => (
                                <th key={col.key} className={`col-${col.key}`}
                                  style={(col.key === 'id' || col.key === 'priority') ? { cursor: 'pointer', userSelect: 'none' } : {}}
                                  onClick={(col.key === 'id' || col.key === 'priority') ? () => handleSort(col.key as 'id' | 'priority') : undefined}
                                >
                                  {col.label}
                                  {(col.key === 'id' || col.key === 'priority') && sortIcon(col.key as 'id' | 'priority')}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredScenarios.map(scenario => (
                              <tr key={scenario.id} className={selectedScenarios.has(scenario.id) ? 'selected-row' : ''}>
                                <td className="col-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={selectedScenarios.has(scenario.id)}
                                    onChange={() => handleToggleScenario(scenario.id)}
                                  />
                                </td>
                                {columns.find(c => c.key === 'id')?.visible && (
                                  <td className="col-id">
                                    <span
                                      className="scenario-id"
                                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                      onClick={() => setViewingScenario(scenario)}
                                    >{scenario.scenario_id}</span>
                                  </td>
                                )}
                          {columns.find(c => c.key === 'preconditions')?.visible && (
                            <td className="col-preconditions">
                              {scenario.preconditions ? <RichTextDisplay html={scenario.preconditions} /> : <span className="no-data">—</span>}
                            </td>
                          )}
                          {columns.find(c => c.key === 'description')?.visible && (
                            <td className="col-description">
                              <div className="description-cell">
                                <strong><RichTextDisplay html={scenario.scenario_description} /></strong>
                              </div>
                            </td>
                          )}
                          {columns.find(c => c.key === 'priority')?.visible && (
                            <td className="col-priority">
                              <span className={`priority-badge priority-${scenario.priority.toLowerCase()}`}>
                                {scenario.priority}
                              </span>
                            </td>
                          )}
                          {columns.find(c => c.key === 'environment')?.visible && (
                            <td className="col-environment">
                              {scenario.environment ? (
                                <span
                                  className="environment-badge"
                                  style={{ backgroundColor: getEnvironmentColor(scenario.environment) }}
                                >
                                  {scenario.environment}
                                </span>
                              ) : (
                                <span className="no-data">—</span>
                              )}
                            </td>
                          )}
                          {columns.find(c => c.key === 'tags')?.visible && (
                            <td className="col-tags">
                              <div className="tags-cell">
                                {/* Fallback "Manual" badge (derived) — only when the scenario has neither
                                    an explicit "AI Generated" nor "Manual" tag, so it never doubles up
                                    with a real "Manual" tag rendered below. */}
                                {(!scenario.tags || (!scenario.tags.includes('AI Generated') && !scenario.tags.includes('Manual'))) && (
                                  <span className="tag-badge tag-manual">Manual</span>
                                )}
                                {(scenario.tags || []).map(tag => (
                                  <span key={tag} className="tag-badge">{tag}</span>
                                ))}
                              </div>
                            </td>
                          )}
                          {columns.find(c => c.key === 'expected')?.visible && (
                            <td className="col-expected">
                              {scenario.expected_result ? <RichTextDisplay html={scenario.expected_result} /> : <span className="no-data">—</span>}
                            </td>
                          )}
                          {columns.find(c => c.key === 'evidence')?.visible && (
                            <td className="col-evidence">
                              {scenario.expected_evidence && scenario.expected_evidence.length > 0 ? (
                                <span className="evidence-count">{scenario.expected_evidence.length} files</span>
                              ) : (
                                <span className="no-data">—</span>
                              )}
                            </td>
                          )}
                          {columns.find(c => c.key === 'created_date')?.visible && (
                            <td className="col-created-date">
                              {new Date(scenario.created_at).toLocaleDateString()}
                            </td>
                          )}
                          {columns.find(c => c.key === 'created_by')?.visible && (
                            <td className="col-created-by">
                              {scenario.created_by}
                            </td>
                          )}
                          {columns.find(c => c.key === 'actions')?.visible && (
                            <td className="col-actions">
                              <div className="action-buttons">
                                {canWrite && (
                                  <button
                                    className="action-btn edit"
                                    onClick={() => handleEditScenario(scenario)}
                                    title="Edit"
                                  >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  </button>
                                )}
                                {canWrite && (
                                  <button
                                    className="action-btn delete"
                                    onClick={() => handleDeleteScenario(scenario.id)}
                                    title="Delete"
                                  >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                                  </button>
                                )}
                                {!canWrite && <span style={{ color: '#d1d5db', fontSize: '12px' }}>—</span>}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              )}
                  </>
                );
              })()}
            </>
          )}
          {!showFolderSearch && !selectedFolderId && (
            <div className="content-empty-state">
              <div className="empty-state-icon">📁</div>
              <h3>Select a Folder</h3>
              <p>Choose a folder from the tree to view and manage test scenarios</p>
              <button
                className="btn btn-secondary"
                style={{ marginTop: '16px' }}
                onClick={() => { ensureLeafFolderSelected(); startTour(); }}
                title="See a guided walkthrough of this page"
              >
                🎯 Take a Tour
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Scenario Form Modal */}
      {showScenarioForm && (
        <div className="sc-modal-overlay" onClick={() => setShowScenarioForm(false)}>
          <div className="sc-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sc-modal-header">
              <div>
                <h2 className="sc-modal-title">{editingScenario ? 'Edit Test Scenario' : 'Create New Test Scenario'}</h2>
                <p className="sc-modal-subtitle">
                  {breadcrumb.map(f => f.name).join(' • ').toUpperCase() || 'TEST SCENARIO'}
                </p>
              </div>
              <button className="sc-modal-close" onClick={() => setShowScenarioForm(false)} title="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <form onSubmit={handleSubmitScenario} className="sc-modal-body">
              {error && <div className="sc-form-error">{error}<button type="button" onClick={() => setError(null)} style={{marginLeft:8,background:'none',border:'none',cursor:'pointer',color:'inherit'}}>×</button></div>}

              {/* Scenario Title */}
              <div className="sc-field">
                <label className="sc-label">SCENARIO TITLE</label>
                <RichTextEditor
                  value={scenarioFormData.scenario_description}
                  onChange={(val) => setScenarioFormData({ ...scenarioFormData, scenario_description: val })}
                  placeholder="e.g. User Authentication Flow - Mobile"
                  minHeight={60}
                  withCode
                />
              </div>

              {/* Pre-condition */}
              <div className="sc-field">
                <label className="sc-label">PRE-CONDITION</label>
                <RichTextEditor
                  value={scenarioFormData.preconditions}
                  onChange={(val) => setScenarioFormData({ ...scenarioFormData, preconditions: val })}
                  placeholder="Describe any setup or state required before this scenario runs..."
                  minHeight={60}
                  withCode
                />
              </div>

              {/* Priority Cards */}
              <div className="sc-field">
                <label className="sc-label">PRIORITY LEVEL</label>
                <div className="sc-priority-cards">
                  {(['Critical', 'High', 'Medium', 'Low'] as Priority[]).map(p => (
                    <button
                      key={p}
                      type="button"
                      className={`sc-priority-card sc-priority-${p.toLowerCase()} ${scenarioFormData.priority === p ? 'sc-priority-card-active' : ''}`}
                      onClick={() => setScenarioFormData({ ...scenarioFormData, priority: p })}
                    >
                      <span className="sc-priority-icon">
                        {p === 'Critical' && <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
                        {p === 'High' && <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="17 11 12 6 7 11"/><polyline points="17 18 12 13 7 18"/></svg>}
                        {p === 'Medium' && <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="13 6 19 12 13 18"/><polyline points="6 6 12 12 6 18"/></svg>}
                        {p === 'Low' && <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="4" y1="6" x2="20" y2="6"/><polyline points="8 12 12 16 16 12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>}
                      </span>
                      <span className="sc-priority-label">{p}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Classification Tags */}
              <div className="sc-field">
                <div className="sc-tags-header">
                  <label className="sc-label">CLASSIFICATION TAGS</label>
                </div>
                <div className="sc-tags-grid">
                  {PREDEFINED_TAGS.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      className={`sc-tag-pill ${scenarioFormData.tags.includes(tag) ? 'sc-tag-pill-active' : ''}`}
                      onClick={() => {
                        if (scenarioFormData.tags.includes(tag)) {
                          setScenarioFormData({ ...scenarioFormData, tags: scenarioFormData.tags.filter(t => t !== tag) });
                        } else {
                          setScenarioFormData({ ...scenarioFormData, tags: [...scenarioFormData.tags, tag] });
                        }
                      }}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Expected Result */}
              <div className="sc-field">
                <label className="sc-label">EXPECTED RESULT</label>
                <RichTextEditor
                  value={scenarioFormData.expected_result}
                  onChange={(val) => setScenarioFormData({ ...scenarioFormData, expected_result: val })}
                  placeholder="Describe the expected outcome..."
                  minHeight={80}
                  withCode
                />
              </div>

              {/* Regression + AI Generated side by side */}
              <div className="sc-toggle-cards-row">
                {/* Regression */}
                <div className="sc-toggle-card">
                  <div className="sc-toggle-card-info">
                    <span className="sc-toggle-card-title">Regression <span style={{ color: '#ef4444' }}>*</span></span>
                    <span className="sc-toggle-card-sub">INCLUDE IN REG SUITE</span>
                  </div>
                  <div className="sc-yn-group">
                    <button
                      type="button"
                      className={`sc-yn-btn ${isRegression === true ? 'sc-yn-active' : ''}`}
                      onClick={() => setIsRegression(isRegression === true ? null : true)}
                    >Yes</button>
                    <button
                      type="button"
                      className={`sc-yn-btn ${isRegression === false ? 'sc-yn-active' : ''}`}
                      onClick={() => setIsRegression(isRegression === false ? null : false)}
                    >No</button>
                  </div>
                </div>

                {/* AI Generated */}
                <div className="sc-toggle-card">
                  <div className="sc-toggle-card-info">
                    <span className="sc-toggle-card-title">AI Generated <span style={{ color: '#ef4444' }}>*</span></span>
                    <span className="sc-toggle-card-sub">ENABLE COPILOT</span>
                  </div>
                  <div className="sc-yn-group">
                    <button
                      type="button"
                      className={`sc-yn-btn ${isAiGenerated === true ? 'sc-yn-active' : ''}`}
                      onClick={() => setIsAiGenerated(isAiGenerated === true ? null : true)}
                    >Yes</button>
                    <button
                      type="button"
                      className={`sc-yn-btn ${isAiGenerated === false ? 'sc-yn-active' : ''}`}
                      onClick={() => setIsAiGenerated(isAiGenerated === false ? null : false)}
                    >No</button>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="sc-modal-footer">
                <button type="submit" className="sc-btn-submit" disabled={isLoading}>
                  {isLoading ? (editingScenario ? 'Saving...' : 'Creating...') : (editingScenario ? 'Save Changes →' : 'Create Scenario →')}
                </button>
                <button type="button" className="sc-btn-cancel" onClick={() => setShowScenarioForm(false)}>
                  Cancel and Discard
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showFolderForm && (
        <div className="modal-overlay" onClick={() => setShowFolderForm(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Subfolder</h3>
              <button onClick={() => setShowFolderForm(false)} className="modal-close">×</button>
            </div>
            
            <form onSubmit={handleSubmitFolder} className="modal-form">
              <div className="form-group">
                <label>Folder Name <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={folderFormData.name}
                  onChange={(e) => setFolderFormData({ ...folderFormData, name: e.target.value })}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Epic ID <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.82rem' }}>(optional)</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={folderFormData.epic_id}
                  onChange={(e) => setFolderFormData({ ...folderFormData, epic_id: e.target.value.toUpperCase() })}
                  placeholder="e.g. PLANNER-EP-12"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={folderFormData.description}
                  onChange={(e) => setFolderFormData({ ...folderFormData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Creating...' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowFolderForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameForm && (
        <div className="modal-overlay" onClick={() => setShowRenameForm(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Rename Folder</h3>
              <button onClick={() => setShowRenameForm(false)} className="modal-close">×</button>
            </div>
            
            <form onSubmit={handleSubmitRename} className="modal-form">
              <div className="form-group">
                <label>Folder Name <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={folderFormData.name}
                  onChange={(e) => setFolderFormData({ ...folderFormData, name: e.target.value })}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Epic ID <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.82rem' }}>(optional)</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={folderFormData.epic_id}
                  onChange={(e) => setFolderFormData({ ...folderFormData, epic_id: e.target.value.toUpperCase() })}
                  placeholder="e.g. PLANNER-EP-12"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={folderFormData.description}
                  onChange={(e) => setFolderFormData({ ...folderFormData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowRenameForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Link Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share Folder Link</h3>
              <button onClick={() => setShowShareModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <p>Share this link to give others access to this folder:</p>
              <div className="share-link-container">
                <input 
                  type="text" 
                  className="form-control" 
                  value={shareLink} 
                  readOnly 
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    navigator.clipboard.writeText(shareLink);
                    alert('Link copied to clipboard!');
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowShareModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showAIModal && selectedFolderId && (
        <AIGenerateModal
          folderId={selectedFolderId}
          type="scenario"
          onClose={() => setShowAIModal(false)}
          onSaved={() => { setShowAIModal(false); loadScenarios(); }}
        />
      )}

      {showImportModal && selectedFolderId && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import Scenarios from Excel</h3>
              <button onClick={() => setShowImportModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <ScenarioImport
                folderId={selectedFolderId}
                onImportComplete={handleImportComplete}
                onCancel={() => setShowImportModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {reviewFolderId && (() => {
        const reviewFolder = folders.find(f => f.id === reviewFolderId);
        return reviewFolder ? (
          <ReviewModal
            folder={reviewFolder}
            allUsers={allUsers}
            mode={reviewMode}
            onClose={() => setReviewFolderId(null)}
            onDone={async () => { setReviewFolderId(null); await loadFolders(); }}
          />
        ) : null;
      })()}

      {viewingScenario && (
        <ScenarioViewModal
          scenario={viewingScenario}
          onClose={() => setViewingScenario(null)}
        />
      )}

      {showBulkEditModal && (
        <BulkEditModal
          count={selectedScenarios.size}
          type="scenario"
          onApply={handleBulkEditScenarios}
          onCancel={() => setShowBulkEditModal(false)}
        />
      )}

      {/* Set Epic ID Modal */}
      {epicModalFolderId && (
        <div className="modal-overlay" onClick={() => setEpicModalFolderId(null)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Set Epic ID</h3>
              <button onClick={() => setEpicModalFolderId(null)} className="modal-close">×</button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Epic ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={epicModalValue}
                  onChange={e => setEpicModalValue(e.target.value.toUpperCase())}
                  placeholder="e.g. PLANNER-EP-12"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveEpicId(); }}
                />
                <small style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: 4 }}>
                  Link this folder to a Jira Epic. Leave empty to clear.
                </small>
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleSaveEpicId} disabled={epicModalSaving}>
                  {epicModalSaving ? 'Saving...' : 'Save'}
                </button>
                <button className="btn btn-secondary" onClick={() => setEpicModalFolderId(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
